// Leitura do payload do JWT no navegador — fonte ÚNICA.
//
// O token guarda a empresa ATIVA (a matriz troca para uma filial via
// /trocar-empresa e recebe um token novo), enquanto o registro do usuário no
// banco continua apontando para a empresa ORIGINAL. Então toda tela que precisa
// saber "em qual empresa eu estou agora" lê daqui — e não de `user.companyId`.
//
// Por que não dá para usar `atob(token.split(".")[1])` direto:
//
// JWT é base64**url** (RFC 7515) — o alfabeto troca `+` por `-` e `/` por `_`, e
// o preenchimento `=` é omitido. `atob()` só aceita base64 padrão e lança
// InvalidCharacterError ao ver `-` ou `_`. Se o payload de UM usuário por acaso
// codificar um desses caracteres, a decodificação falha para ELE, sempre — o
// nome, o e-mail, o id e a empresa dele são fixos, então o payload é estável e
// o erro é determinístico. Medindo com payloads realistas: ~5% dos usuários.
//
// Como todo ponto de chamada tinha um `catch` que caía para `user.companyId`
// (a empresa ORIGINAL), o sintoma não era um erro na tela: era um usuário —
// normalmente um admin da matriz, que tem o payload mais gordo — preso na
// matriz. O seletor de empresas não saía do lugar, e a página de WhatsApp
// mandava o companyId da matriz enquanto o backend, que lê o token de verdade,
// enxergava a filial — o que devolve 404 de sessão para sempre.
//
// A conversão para base64 padrão + repadding resolve; o `catch` continua aqui
// para token ausente/deformado, mas deixa de ser o caminho comum.
export const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1] || "";
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

// Empresa ativa segundo o token. `null` quando não há token legível — quem
// chama decide o fallback (normalmente `user.companyId`).
export const getActiveCompanyIdFromToken = (token) => {
  const raw =
    token ??
    (typeof window !== "undefined" ? localStorage.getItem("token") : null);
  const payload = decodeJwtPayload(raw);
  return (
    payload?.user?.companyId ??
    payload?.companyId ??
    payload?.company?.id ??
    payload?.company?.companyId ??
    null
  );
};

// Empresa ativa para telas que montam URL/chave com o id da empresa: o token
// primeiro e só então `user.companyId`, que `/user-current` devolve como a
// empresa de ORIGEM do usuário (não muda ao trocar de filial).
export const getActiveCompanyId = (user) =>
  getActiveCompanyIdFromToken() ?? user?.companyId ?? null;
